import { datadogRum } from '@datadog/browser-rum';
import { FUUL_PROGRAM_IDL } from '@fuul/sdk-solana';

export enum SolanaErrorCode {
  UNKNOWN = 'UNKNOWN',
  WALLET_NOT_CONNECTED = 'WALLET_NOT_CONNECTED',
  INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
  USER_REJECTED = 'USER_REJECTED',
  TRANSACTION_FAILED = 'TRANSACTION_FAILED',
  SIMULATION_FAILED = 'SIMULATION_FAILED',
  INVALID_METADATA_URI = 'INVALID_METADATA_URI',
  ACCOUNT_ALREADY_EXISTS = 'ACCOUNT_ALREADY_EXISTS',
}

export interface SanitizedError {
  message: string;
  code: SolanaErrorCode;
}

/** Minimal `PublicKey` shape, so this display helper pulls in no Solana runtime dependency. */
interface PublicKeyLike {
  toBase58?: () => string;
  toString: () => string;
}

interface InstructionLike {
  programId?: PublicKeyLike | string | null;
}

/** A `Transaction`, or just its instruction list. Only `programId` is ever read. */
export type SolanaTransactionLike = { instructions?: readonly InstructionLike[] | null } | readonly InstructionLike[];

/**
 * Optional context for `sanitizeSolanaError`. Extra keys are forwarded to Datadog as before;
 * `transaction` is consumed locally to tell which program a low `Custom` code belongs to.
 */
export interface SolanaErrorContext extends Record<string, unknown> {
  transaction?: SolanaTransactionLike | null;
}

/**
 * Thrown when a pre-flight simulation reports an on-chain failure. Keeps the raw `err` and
 * `logs` on the instance so `sanitizeSolanaError` can decode them structurally.
 */
export class SolanaSimulationError extends Error {
  readonly err: unknown;
  readonly logs: string[] | null;

  constructor(err: unknown, logs: string[] | null = null) {
    super(`Transaction simulation failed: ${JSON.stringify(err)}`);
    this.name = 'SolanaSimulationError';
    this.err = err;
    this.logs = logs;
  }
}

/** Anchor reserves custom codes below 6000; program-defined errors start at 6000. */
const PROGRAM_ERROR_CODE_START = 6000;

/**
 * Canonical program ids, as base58 literals so this module stays dependency-free.
 * Match `SystemProgram.programId` (@solana/web3.js/lib/index.cjs.js:3158) and
 * `TOKEN_PROGRAM_ID` / `TOKEN_2022_PROGRAM_ID` (@solana/spl-token/lib/cjs/constants.js:7,9).
 */
const SYSTEM_PROGRAM_ID = '11111111111111111111111111111111';
const TOKEN_PROGRAM_ID = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
const TOKEN_2022_PROGRAM_ID = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';

/**
 * Rent exemption for the ~3604-byte Project account (~0.052) plus transaction fees, denominated
 * in the network's native token. Every network this program is deployed to charges the same
 * lamport-equivalent rent, so a single estimate covers all of them.
 */
const PROJECT_CREATION_NATIVE_TOKEN_ESTIMATE = '0.055';

const INSUFFICIENT_FUNDS_MESSAGE =
  'Insufficient funds to cover this transaction. Creating the project account requires about ' +
  `${PROJECT_CREATION_NATIVE_TOKEN_ESTIMATE} in the network's native token for account rent plus ` +
  'fees. Add funds to your wallet and try again.';

const INSUFFICIENT_TOKEN_BALANCE_MESSAGE =
  'Insufficient token balance to complete this deposit. Add funds to your wallet and try again.';

const NOT_RENT_EXEMPT_MESSAGE =
  "This account is below the rent-exempt minimum. Add funds in the network's native token and try again.";

/** Correct whichever program produced the code: no denomination, no amount, no rent claim. */
const AMBIGUOUS_INSUFFICIENT_FUNDS_MESSAGE =
  'Insufficient funds to complete this transaction. Check your balances and try again.';

const INSUFFICIENT_FEE_FUNDS_MESSAGE = "Insufficient funds in the network's native token to cover transaction fees.";

const ACCOUNT_ALREADY_EXISTS_MESSAGE = 'This project is already initialized on this network.';

const GENERIC_FAILURE_MESSAGE = 'Transaction failed. Please try again.';

/**
 * A `Custom` code is program-defined: the same number means different things depending on which
 * program produced it. `create_project` reaches the System Program through its `init, payer`
 * CPI, while the deposit flows reach the SPL Token Program — so the code alone is not enough.
 */
const SYSTEM_PROGRAM_ERRORS: Record<number, SanitizedError> = {
  // AccountAlreadyInUse
  0: { message: ACCOUNT_ALREADY_EXISTS_MESSAGE, code: SolanaErrorCode.ACCOUNT_ALREADY_EXISTS },
  // ResultWithNegativeLamports
  1: { message: INSUFFICIENT_FUNDS_MESSAGE, code: SolanaErrorCode.INSUFFICIENT_BALANCE },
};

const TOKEN_PROGRAM_ERRORS: Record<number, SanitizedError> = {
  // NotRentExempt
  0: { message: NOT_RENT_EXEMPT_MESSAGE, code: SolanaErrorCode.INSUFFICIENT_BALANCE },
  // InsufficientFunds
  1: { message: INSUFFICIENT_TOKEN_BALANCE_MESSAGE, code: SolanaErrorCode.INSUFFICIENT_BALANCE },
};

/**
 * Used when the failing program could not be resolved, or resolved to a program with no table of
 * its own — most often the Fuul program itself, which only ever reports a sub-6000 code on behalf
 * of an inner CPI (Anchor numbers its own errors from 6000). Code 1 means "not enough of
 * something" under both the System and the Token program, so a denomination-free message is safe;
 * code 0 means two unrelated things, so it stays generic rather than guessing.
 */
const AMBIGUOUS_PROGRAM_ERRORS: Record<number, SanitizedError> = {
  1: { message: AMBIGUOUS_INSUFFICIENT_FUNDS_MESSAGE, code: SolanaErrorCode.INSUFFICIENT_BALANCE },
};

interface IdlErrorEntry {
  code: number;
  name: string;
  msg?: string;
}

/** Keys under which wallets and RPC clients nest the raw transaction error. */
const NESTED_ERROR_KEYS = ['err', 'error', 'cause', 'transactionError', 'value', 'data'] as const;

const MAX_NESTED_ERROR_DEPTH = 4;

/**
 * Matches a serialized InstructionError tuple, either as a whole JSON payload or embedded in a
 * longer message: {"InstructionError":[2,{"Custom":1}]} / {"InstructionError":[0,"BorshIoError"]}.
 */
const INSTRUCTION_ERROR_PATTERN =
  /"InstructionError"\s*:\s*\[\s*(\d+)\s*,\s*(?:\{\s*"Custom"\s*:\s*(\d+)\s*\}|"([A-Za-z][A-Za-z0-9]*)")\s*\]/;

/**
 * Matches the hexadecimal form wallets surface instead of the tuple, with or without the
 * instruction index: `Error processing Instruction 2: custom program error: 0x1` / `0x1770`.
 */
const HEX_CUSTOM_ERROR_PATTERN =
  /(?:Error processing Instruction\s+(\d+)\s*:\s*)?custom program error:\s*0x([0-9a-fA-F]+)/i;

/** `Program <id> failed: custom program error: 0x1` — the innermost failure is logged first. */
const PROGRAM_FAILURE_PATTERN =
  /Program ([1-9A-HJ-NP-Za-km-z]{32,44}) failed(?::\s*custom program error:\s*0x([0-9a-fA-F]+))?/g;

type InstructionErrorDetail = { custom: number } | { variant: string };

interface DecodedInstructionError {
  /** `null` when the source text carried a code but no instruction index. */
  instructionIndex: number | null;
  detail: InstructionErrorDetail;
}

type TextParser = (text: string) => DecodedInstructionError | null;

/**
 * Fuul program errors keyed by code, read from the IDLs shipped by @fuul/sdk-solana.
 * The tables are identical across every network the SDK ships, so the first entry wins.
 * Built lazily so a missing/partial IDL export can never break module load.
 */
let programErrorTable: Map<number, IdlErrorEntry> | null = null;

function getProgramError(code: number): IdlErrorEntry | null {
  if (!programErrorTable) {
    programErrorTable = new Map<number, IdlErrorEntry>();
    const idlsByNetwork = FUUL_PROGRAM_IDL as Record<string, { errors?: IdlErrorEntry[] }> | undefined;

    for (const idl of Object.values(idlsByNetwork ?? {})) {
      for (const entry of idl?.errors ?? []) {
        if (typeof entry?.code === 'number' && !programErrorTable.has(entry.code)) {
          programErrorTable.set(entry.code, entry);
        }
      }
    }
  }

  return programErrorTable.get(code) ?? null;
}

function readInstructionErrorTuple(value: object): DecodedInstructionError | null {
  const tuple = (value as Record<string, unknown>).InstructionError;
  if (!Array.isArray(tuple) || tuple.length < 2) return null;

  const [instructionIndex, detail] = tuple;
  if (typeof instructionIndex !== 'number') return null;

  if (typeof detail === 'string') {
    return { instructionIndex, detail: { variant: detail } };
  }

  if (detail && typeof detail === 'object') {
    const custom = (detail as Record<string, unknown>).Custom;
    if (typeof custom === 'number') {
      return { instructionIndex, detail: { custom } };
    }
  }

  return null;
}

function parseInstructionErrorTuple(text: string): DecodedInstructionError | null {
  const match = INSTRUCTION_ERROR_PATTERN.exec(text);
  if (!match) return null;

  const [, rawIndex, rawCustom, rawVariant] = match;
  const instructionIndex = Number(rawIndex);

  if (rawCustom !== undefined) {
    return { instructionIndex, detail: { custom: Number(rawCustom) } };
  }

  return { instructionIndex, detail: { variant: rawVariant ?? 'Unknown' } };
}

function parseHexCustomError(text: string): DecodedInstructionError | null {
  const match = HEX_CUSTOM_ERROR_PATTERN.exec(text);
  if (!match) return null;

  const [, rawIndex, rawHex] = match;
  const custom = Number.parseInt(rawHex, 16);
  if (!Number.isFinite(custom)) return null;

  return { instructionIndex: rawIndex === undefined ? null : Number(rawIndex), detail: { custom } };
}

/**
 * Wallets and RPC clients surface the same failure several ways: as a real object, as text in a
 * message, or only inside `logs`. All of them are searched with the given text parser.
 */
function decodeInstructionError(error: unknown, parseText: TextParser, depth = 0): DecodedInstructionError | null {
  if (error == null || depth > MAX_NESTED_ERROR_DEPTH) return null;

  if (typeof error === 'string') return parseText(error);
  if (typeof error !== 'object') return null;

  const fromTuple = readInstructionErrorTuple(error);
  if (fromTuple) return fromTuple;

  const obj = error as Record<string, unknown>;

  if (typeof obj.message === 'string') {
    const fromMessage = parseText(obj.message);
    if (fromMessage) return fromMessage;
  }

  const logs = extractErrorLogs(error);
  if (logs) {
    const fromLogs = parseText(logs);
    if (fromLogs) return fromLogs;
  }

  for (const key of NESTED_ERROR_KEYS) {
    const fromNested = decodeInstructionError(obj[key], parseText, depth + 1);
    if (fromNested) return fromNested;
  }

  return null;
}

function toBase58(programId: unknown): string | null {
  if (typeof programId === 'string') return programId;

  if (programId && typeof programId === 'object') {
    const key = programId as PublicKeyLike;
    if (typeof key.toBase58 === 'function') return key.toBase58();
    if (typeof key.toString === 'function') {
      const value = key.toString();
      return value === '[object Object]' ? null : value;
    }
  }

  return null;
}

/**
 * Reads the failing program off the transaction the caller built. Only usable when the failing
 * instruction is a top-level one — a CPI reports the OUTER index with the INNER program's code.
 */
function readProgramIdFromContext(
  transaction: SolanaTransactionLike | null | undefined,
  instructionIndex: number | null,
): string | null {
  if (!transaction || instructionIndex === null || instructionIndex < 0) return null;

  const instructions = Array.isArray(transaction)
    ? (transaction as readonly InstructionLike[])
    : (transaction as { instructions?: readonly InstructionLike[] | null }).instructions;

  if (!Array.isArray(instructions)) return null;

  const instruction = instructions[instructionIndex];
  return instruction ? toBase58(instruction.programId) : null;
}

/**
 * Reads the failing program off the simulation logs. Unlike the instruction list this survives a
 * CPI: the runtime logs the innermost `Program <id> failed` line first.
 */
function resolveProgramIdFromLogs(logText: string, custom: number | null): string | null {
  if (!logText) return null;

  PROGRAM_FAILURE_PATTERN.lastIndex = 0;
  let firstFailure: string | null = null;
  let match: RegExpExecArray | null;

  while ((match = PROGRAM_FAILURE_PATTERN.exec(logText)) !== null) {
    const [, programId, rawHex] = match;
    if (firstFailure === null) firstFailure = programId;
    if (rawHex !== undefined && custom !== null && Number.parseInt(rawHex, 16) === custom) {
      return programId;
    }
  }

  return firstFailure;
}

function errorTableFor(programId: string | null): Record<number, SanitizedError> {
  if (programId === SYSTEM_PROGRAM_ID) return SYSTEM_PROGRAM_ERRORS;
  if (programId === TOKEN_PROGRAM_ID || programId === TOKEN_2022_PROGRAM_ID) return TOKEN_PROGRAM_ERRORS;
  return AMBIGUOUS_PROGRAM_ERRORS;
}

function mapInstructionError({ detail }: DecodedInstructionError, programId: string | null): SanitizedError {
  if ('variant' in detail) {
    if (detail.variant === 'InsufficientFundsForRent') {
      return { message: INSUFFICIENT_FUNDS_MESSAGE, code: SolanaErrorCode.INSUFFICIENT_BALANCE };
    }
    return {
      message: `Transaction failed (${detail.variant}). Please try again.`,
      code: SolanaErrorCode.TRANSACTION_FAILED,
    };
  }

  const { custom } = detail;

  // Program-defined codes are unambiguous: only the Fuul program numbers errors from 6000 up.
  if (custom >= PROGRAM_ERROR_CODE_START) {
    const programError = getProgramError(custom);
    if (programError) {
      return {
        message: programError.msg ?? `Transaction failed (${programError.name}). Please try again.`,
        code: SolanaErrorCode.TRANSACTION_FAILED,
      };
    }
  }

  const mapped = errorTableFor(programId)[custom];
  if (mapped) return mapped;

  return {
    message: `Transaction failed (error code ${custom}). Please try again.`,
    code: SolanaErrorCode.TRANSACTION_FAILED,
  };
}

function extractErrorLogs(error: unknown): string {
  if (typeof error !== 'object' || error === null) return '';
  const logs = (error as Record<string, unknown>).logs;
  return Array.isArray(logs) ? logs.join(' | ') : '';
}

/** Every `logs` array found while walking the error, including nested wrappers. */
function collectLogText(error: unknown, depth = 0): string {
  if (error == null || typeof error !== 'object' || depth > MAX_NESTED_ERROR_DEPTH) return '';

  const obj = error as Record<string, unknown>;
  const parts: string[] = [];

  const own = extractErrorLogs(error);
  if (own) parts.push(own);

  for (const key of NESTED_ERROR_KEYS) {
    const nested = collectLogText(obj[key], depth + 1);
    if (nested) parts.push(nested);
  }

  return parts.join(' | ');
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null) {
    const obj = error as Record<string, unknown>;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.msg === 'string') return obj.msg;
    if (Array.isArray(obj.logs)) return `Logs: ${obj.logs.join(' | ')}`;
    return JSON.stringify(error);
  }
  return String(error);
}

/**
 * Sanitize Solana errors for user display.
 * Logs full error to Datadog, returns user-friendly message.
 */
export function sanitizeSolanaError(error: unknown, context?: SolanaErrorContext): SanitizedError {
  const { transaction, ...loggableContext } = context ?? {};
  const errorMessage = extractErrorMessage(error);
  const err = error instanceof Error ? error : new Error(errorMessage);

  console.error('[Solana Error] Raw error object:', error);
  console.error('[Solana Error] Extracted message:', errorMessage);

  // Must run before the substring matching below: a serialized InstructionError matches none
  // of those keywords and would otherwise fall through to UNKNOWN. The JSON tuple is tried
  // first because it carries the instruction index; the hex form is the wallet-surfaced variant.
  const instructionError =
    decodeInstructionError(error, parseInstructionErrorTuple) ?? decodeInstructionError(error, parseHexCustomError);

  const logText = collectLogText(error);
  const custom = instructionError && 'custom' in instructionError.detail ? instructionError.detail.custom : null;
  const failingProgramId = instructionError
    ? (resolveProgramIdFromLogs(logText, custom) ??
      readProgramIdFromContext(transaction, instructionError.instructionIndex))
    : null;

  // Log full error to Datadog — include raw error for non-Error objects
  const ddContext = {
    context: 'solana-deployment',
    rawError: error instanceof Error ? undefined : JSON.stringify(error),
    extractedMessage: errorMessage,
    failingProgramId,
    ...loggableContext,
  };
  datadogRum.addError(err, ddContext);

  if (instructionError) {
    return mapInstructionError(instructionError, failingProgramId);
  }

  // Map to user-friendly messages. Logs usually carry the human-readable reason, so they
  // are matched alongside the message.
  const msg = `${errorMessage} ${logText}`.toLowerCase();

  if (msg.includes('user rejected') || msg.includes('user denied')) {
    return { message: 'Transaction was cancelled', code: SolanaErrorCode.USER_REJECTED };
  }

  if (msg.includes('insufficient') || msg.includes('not enough')) {
    return { message: INSUFFICIENT_FEE_FUNDS_MESSAGE, code: SolanaErrorCode.INSUFFICIENT_BALANCE };
  }

  if (msg.includes('simulation failed')) {
    return { message: 'Transaction simulation failed. Please try again.', code: SolanaErrorCode.SIMULATION_FAILED };
  }

  if (msg.includes('blockhash not found') || msg.includes('expired')) {
    return { message: 'Transaction expired. Please try again.', code: SolanaErrorCode.TRANSACTION_FAILED };
  }

  return { message: GENERIC_FAILURE_MESSAGE, code: SolanaErrorCode.UNKNOWN };
}

/**
 * Handle Solana errors with toast notifications.
 * Follows pattern from contract-errors.ts
 */
export function handleSolanaErrorToast(error: unknown, context?: SolanaErrorContext): SanitizedError {
  const { message, code } = sanitizeSolanaError(error, context);
  // Toast is handled by the hook - this just returns the sanitized error
  return { message, code };
}
